import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Save, Trash2, Copy, Edit2, AlertCircle, CheckCircle,
  RefreshCw, Key, Play, Pause, ExternalLink, Clock,
  ChevronDown, ChevronUp, RotateCcw
} from 'lucide-react';
import { Webhook, WebhookDelivery, apiService, Index } from '../services/api';
import {
  buildAsyncRequestOwnerKey,
  isCurrentAsyncRequest,
} from '../utils/asyncRequestOwnership';
import {
  clearPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import { useTabActivity } from '../contexts/TabActivityContext';
import './WebhookDetailSidebar.css';

interface WebhookDetailSidebarProps {
  webhook: Webhook | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (webhook: Webhook) => void;
  onDelete?: (webhookId: string) => void;
  isCreating?: boolean;
  availableEvents?: string[];
  indexes?: Index[];
}

const WEBHOOK_PENDING_ATTEMPT_NAMESPACE = 'webhook-management';

interface CompletedWebhookCredentialAttempt {
  operation: 'create' | 'rotate-secret';
  attempt: PendingIdempotencyAttempt;
  responseOwnerId: string;
}

const WebhookDetailSidebar: React.FC<WebhookDetailSidebarProps> = ({
  webhook,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isCreating = false,
  availableEvents = [],
  indexes = []
}) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('webhookSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isRotatingSecret, setIsRotatingSecret] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Form fields
  const [webhookName, setWebhookName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedIndexIds, setSelectedIndexIds] = useState<string[]>([]);
  const [webhookStatus, setWebhookStatus] = useState<'active' | 'paused'>('active');
  const [newSecret, setNewSecret] = useState<string | null>(null);

  // Deliveries
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [deliveryFilter, setDeliveryFilter] = useState<string>('');
  const [expandedDeliveries, setExpandedDeliveries] = useState<Set<string>>(new Set());

  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const displayedSecretOwnerIdRef = useRef<string | null>(null);
  const deliveryRequestGenerationRef = useRef(0);
  const createAttemptRef = useRef<PendingIdempotencyAttempt | null>(null);
  const saveRequestInFlightRef = useRef(false);
  const secretRotationAttemptRef = useRef<PendingIdempotencyAttempt | null>(null);
  const secretRotationInFlightRef = useRef(false);
  const isMountedRef = useRef(false);
  const credentialRequestGenerationRef = useRef(0);
  const completedCredentialAttemptRef = useRef<
  CompletedWebhookCredentialAttempt | null
  >(null);
  const credentialOwnerKey = buildAsyncRequestOwnerKey(
    isOpen ? 'open' : 'closed',
    isCreating ? 'create' : 'manage',
    isCreating ? null : webhook?.webhook_id,
  );
  const credentialOwnerKeyRef = useRef(credentialOwnerKey);
  if (credentialOwnerKeyRef.current !== credentialOwnerKey) {
    credentialOwnerKeyRef.current = credentialOwnerKey;
    credentialRequestGenerationRef.current += 1;
  }
  const deliveryOwnerKeyRef = useRef(
    buildAsyncRequestOwnerKey(
      webhook && !isCreating ? webhook.webhook_id : null,
      deliveryFilter,
    ),
  );
  deliveryOwnerKeyRef.current = buildAsyncRequestOwnerKey(
    webhook && !isCreating ? webhook.webhook_id : null,
    deliveryFilter,
  );
  const deliveryFilterRef = useRef(deliveryFilter);
  deliveryFilterRef.current = deliveryFilter;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      credentialRequestGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const completed = completedCredentialAttemptRef.current;
    if (
      !isOpen
      || !newSecret
      || !completed
      || displayedSecretOwnerIdRef.current !== completed.responseOwnerId
    ) {
      return;
    }
    clearPendingIdempotencyAttempt({
      namespace: WEBHOOK_PENDING_ATTEMPT_NAMESPACE,
      operation: completed.operation,
      requestFingerprint: completed.attempt.requestFingerprint,
      expectedIdempotencyKey: completed.attempt.idempotencyKey,
    });
    if (
      completed.operation === 'create'
      && createAttemptRef.current?.idempotencyKey
        === completed.attempt.idempotencyKey
    ) {
      createAttemptRef.current = null;
    }
    if (
      completed.operation === 'rotate-secret'
      && secretRotationAttemptRef.current?.idempotencyKey
        === completed.attempt.idempotencyKey
    ) {
      secretRotationAttemptRef.current = null;
    }
    completedCredentialAttemptRef.current = null;
  }, [isCreating, isOpen, newSecret, webhook?.webhook_id]);

  const loadDeliveries = useCallback(async (webhookId: string, filter: string = '') => {
    const token = {
      generation: deliveryRequestGenerationRef.current + 1,
      ownerKey: buildAsyncRequestOwnerKey(webhookId, filter),
    };
    if (token.ownerKey !== deliveryOwnerKeyRef.current) {
      return;
    }
    deliveryRequestGenerationRef.current = token.generation;
    setDeliveries([]);
    setDeliveryError(null);
    setIsLoadingDeliveries(true);
    try {
      const data = await apiService.getWebhookDeliveries(webhookId, filter || undefined, 100);
      if (isCurrentAsyncRequest(
        deliveryRequestGenerationRef.current,
        deliveryOwnerKeyRef.current,
        token,
      )) {
        setDeliveries(data);
      }
    } catch (error) {
      if (isCurrentAsyncRequest(
        deliveryRequestGenerationRef.current,
        deliveryOwnerKeyRef.current,
        token,
      )) {
        console.error('Failed to load deliveries:', error);
        setDeliveries([]);
        setDeliveryError('Unable to load deliveries for the selected filter.');
      }
    } finally {
      if (isCurrentAsyncRequest(
        deliveryRequestGenerationRef.current,
        deliveryOwnerKeyRef.current,
        token,
      )) {
        setIsLoadingDeliveries(false);
      }
    }
  }, []);

  // Initialize form from webhook
  useEffect(() => {
    if (webhook && !isCreating) {
      setWebhookName(webhook.name);
      setWebhookUrl(webhook.url);
      setSelectedEvents(webhook.events || []);
      setSelectedIndexIds(webhook.index_ids || []);
      setWebhookStatus(webhook.status === 'disabled' ? 'paused' : webhook.status as 'active' | 'paused');
      setIsEditing(false);
      if (displayedSecretOwnerIdRef.current !== webhook.webhook_id) {
        displayedSecretOwnerIdRef.current = null;
        setNewSecret(null);
      }
      loadDeliveries(webhook.webhook_id, deliveryFilterRef.current);
    } else if (isCreating) {
      deliveryRequestGenerationRef.current += 1;
      setWebhookName('');
      setWebhookUrl('');
      setSelectedEvents([]);
      setSelectedIndexIds([]);
      setWebhookStatus('active');
      setIsEditing(true);
      setDeliveries([]);
      setDeliveryError(null);
      setIsLoadingDeliveries(false);
      displayedSecretOwnerIdRef.current = null;
      setNewSecret(null);
    } else {
      deliveryRequestGenerationRef.current += 1;
      setDeliveries([]);
      setDeliveryError(null);
      setIsLoadingDeliveries(false);
    }
  }, [isCreating, loadDeliveries, webhook]);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEditing) {
        onClose();
      }
    };

    if (isOpen && isActive) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isActive, isOpen, isEditing, onClose]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('webhookSidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('webhookSidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-webhook-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(1400, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-webhook-sidebar');
    };
  }, [isActive, isOpen, isResizing]);

  // Toggle event selection
  const toggleEvent = (event: string) => {
    setSelectedEvents(prev =>
      prev.includes(event)
        ? prev.filter(e => e !== event)
        : [...prev, event]
    );
  };

  // Toggle index selection
  const toggleIndex = (indexId: string) => {
    setSelectedIndexIds(prev =>
      prev.includes(indexId)
        ? prev.filter(id => id !== indexId)
        : [...prev, indexId]
    );
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!webhookName.trim()) return 'Webhook name is required';
    if (webhookName.trim().length < 1 || webhookName.trim().length > 100) {
      return 'Webhook name must be between 1 and 100 characters';
    }
    if (!webhookUrl.trim()) return 'URL is required';
    if (!webhookUrl.startsWith('https://')) return 'URL must use HTTPS';
    if (selectedEvents.length === 0) return 'At least one event must be selected';
    return null;
  };

  // Save webhook
  const handleSave = async () => {
    if (saveRequestInFlightRef.current) return;

    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    saveRequestInFlightRef.current = true;
    setIsSaving(true);
    setMessage(null);
    const requestToken = {
      generation: credentialRequestGenerationRef.current + 1,
      ownerKey: credentialOwnerKeyRef.current,
    };
    credentialRequestGenerationRef.current = requestToken.generation;
    const ownsRequest = () => (
      isMountedRef.current
      && isCurrentAsyncRequest(
        credentialRequestGenerationRef.current,
        credentialOwnerKeyRef.current,
        requestToken,
      )
    );

    try {
      let savedWebhook: Webhook;
      if (isCreating || !webhook) {
        const request = {
          name: webhookName.trim(),
          url: webhookUrl.trim(),
          events: [...selectedEvents].sort(),
          index_ids: selectedIndexIds.length > 0
            ? [...selectedIndexIds].sort()
            : null,
        };
        const attempt = await getOrCreatePendingIdempotencyAttempt({
          namespace: WEBHOOK_PENDING_ATTEMPT_NAMESPACE,
          operation: 'create',
          requestPayload: request,
          keyPrefix: 'webhook-create',
          inMemoryAttempt: createAttemptRef.current || undefined,
        });
        createAttemptRef.current = attempt;
        if (!ownsRequest()) {
          return;
        }
        const response = await apiService.createWebhook(
          request,
          attempt.idempotencyKey,
        );
        if (!ownsRequest()) {
          return;
        }
        savedWebhook = response;
        completedCredentialAttemptRef.current = {
          operation: 'create',
          attempt,
          responseOwnerId: response.webhook_id,
        };
        displayedSecretOwnerIdRef.current = response.webhook_id;
        setNewSecret(response.secret);
        setMessage({ type: 'success', text: 'Webhook created! Save the secret below - it will only be shown once.' });
      } else {
        savedWebhook = await apiService.updateWebhook(webhook.webhook_id, {
          name: webhookName.trim(),
          url: webhookUrl.trim(),
          events: selectedEvents,
          index_ids: selectedIndexIds.length > 0 ? selectedIndexIds : null,
          status: webhookStatus
        });
        setMessage({ type: 'success', text: 'Webhook updated successfully!' });
      }

      setIsEditing(false);
      if (onSave) {
        onSave(savedWebhook);
      }

      if (!isCreating) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }

    } catch (error: any) {
      if (!ownsRequest()) {
        return;
      }
      console.error('Failed to save webhook:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save webhook. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      saveRequestInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  // Delete webhook
  const handleDelete = async () => {
    if (!webhook) return;

    if (!window.confirm(`Are you sure you want to delete webhook "${webhook.name}"? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await apiService.deleteWebhook(webhook.webhook_id);
      setMessage({ type: 'success', text: 'Webhook deleted successfully!' });
      if (onDelete) {
        onDelete(webhook.webhook_id);
      }
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete webhook.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsDeleting(false);
    }
  };

  // Test webhook
  const handleTest = async () => {
    if (!webhook) return;

    setIsTesting(true);
    setMessage(null);
    try {
      const result = await apiService.testWebhook(webhook.webhook_id);
      if (result.success) {
        setMessage({ type: 'success', text: `Test successful! Status: ${result.status_code}` });
      } else {
        setMessage({ type: 'error', text: `Test failed: ${result.error || 'Unknown error'}` });
      }
      loadDeliveries(webhook.webhook_id, deliveryFilter);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to test webhook.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsTesting(false);
    }
  };

  // Rotate secret
  const handleRotateSecret = async () => {
    if (!webhook || secretRotationInFlightRef.current) return;

    if (!window.confirm('Are you sure you want to rotate the secret? You will need to update your endpoint with the new secret.')) {
      return;
    }

    const rotatedWebhookId = webhook.webhook_id;
    secretRotationInFlightRef.current = true;
    setIsRotatingSecret(true);
    const requestToken = {
      generation: credentialRequestGenerationRef.current + 1,
      ownerKey: credentialOwnerKeyRef.current,
    };
    credentialRequestGenerationRef.current = requestToken.generation;
    const ownsRequest = () => (
      isMountedRef.current
      && isCurrentAsyncRequest(
        credentialRequestGenerationRef.current,
        credentialOwnerKeyRef.current,
        requestToken,
      )
    );
    try {
      const request = { webhook_id: rotatedWebhookId };
      const attempt = await getOrCreatePendingIdempotencyAttempt({
        namespace: WEBHOOK_PENDING_ATTEMPT_NAMESPACE,
        operation: 'rotate-secret',
        requestPayload: request,
        keyPrefix: `webhook-secret-rotate:${rotatedWebhookId}`,
        inMemoryAttempt: secretRotationAttemptRef.current || undefined,
        ttlMs: ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS,
      });
      secretRotationAttemptRef.current = attempt;
      if (!ownsRequest()) {
        return;
      }
      const result = await apiService.rotateWebhookSecret(
        rotatedWebhookId,
        attempt.idempotencyKey,
      );
      if (!ownsRequest()) {
        return;
      }
      completedCredentialAttemptRef.current = {
        operation: 'rotate-secret',
        attempt,
        responseOwnerId: result.webhook_id,
      };
      displayedSecretOwnerIdRef.current = result.webhook_id;
      setNewSecret(result.new_secret);
      setMessage({ type: 'success', text: 'Secret rotated! Save the new secret below - it will only be shown once.' });
    } catch (error: any) {
      if (!ownsRequest()) {
        return;
      }
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to rotate secret.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      secretRotationInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsRotatingSecret(false);
      }
    }
  };

  // Retry delivery
  const handleRetryDelivery = async (deliveryId: string) => {
    try {
      await apiService.retryWebhookDelivery(deliveryId);
      if (webhook) {
        loadDeliveries(webhook.webhook_id, deliveryFilter);
      }
      setMessage({ type: 'success', text: 'Delivery retry initiated' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to retry delivery.';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  // Toggle delivery expansion
  const toggleDeliveryExpansion = (deliveryId: string) => {
    setExpandedDeliveries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deliveryId)) {
        newSet.delete(deliveryId);
      } else {
        newSet.add(deliveryId);
      }
      return newSet;
    });
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Copied to clipboard!' });
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to copy' });
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Get delivery status color
  const getDeliveryStatusClass = (status: string) => {
    switch (status) {
      case 'delivered': return 'status-delivered';
      case 'failed': return 'status-failed';
      case 'pending': return 'status-pending';
      case 'retrying': return 'status-retrying';
      default: return '';
    }
  };

  // Group events by category
  const groupedEvents = availableEvents.reduce((acc, event) => {
    const category = event.split('.')[0];
    if (!acc[category]) acc[category] = [];
    acc[category].push(event);
    return acc;
  }, {} as Record<string, string[]>);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`webhook-detail-sidebar ${isOpen ? 'sidebar-open' : ''} ${isResizing ? 'resizing' : ''}`}
        ref={modalRef}
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Resize handle */}
        <div
          className={`resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
        />

        <div className="sidebar-header">
          <h2>{isCreating ? 'Create New Webhook' : isEditing ? 'Edit Webhook' : 'Webhook Details'}</h2>
          <div className="header-actions">
            {!isCreating && !isEditing && webhook && (
              <>
                <button
                  className="action-button"
                  onClick={handleTest}
                  disabled={isTesting}
                  title="Send Test Event"
                >
                  {isTesting ? <RefreshCw size={16} className="spinning" /> : <Play size={16} />}
                </button>
                <button
                  className="action-button"
                  onClick={() => setIsEditing(true)}
                  title="Edit Webhook"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  className="action-button danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title="Delete Webhook"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        {/* New Secret Display */}
        {newSecret && (
          <div className="secret-display">
            <div className="secret-header">
              <Key size={16} />
              <span>Signing Secret (save this - shown only once)</span>
            </div>
            <div className="secret-value">
              <code>{newSecret}</code>
              <button onClick={() => copyToClipboard(newSecret)} title="Copy secret">
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="sidebar-content">
          {/* Basic Info Section */}
          <div className="section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label>Name *</label>
              {isEditing ? (
                <input
                  type="text"
                  value={webhookName}
                  onChange={(e) => setWebhookName(e.target.value)}
                  placeholder="Enter webhook name..."
                  maxLength={100}
                />
              ) : (
                <div className="readonly-value">{webhookName}</div>
              )}
            </div>

            <div className="form-group">
              <label>URL * (HTTPS required)</label>
              {isEditing ? (
                <input
                  type="text"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-endpoint.com/webhook"
                />
              ) : (
                <div className="readonly-value url-value">
                  <span>{webhookUrl}</span>
                  <a href={webhookUrl} target="_blank" rel="noopener noreferrer" title="Open URL">
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>

            {!isCreating && (
              <div className="form-group">
                <label>Status</label>
                {isEditing ? (
                  <div className="status-toggle">
                    <button
                      className={`status-btn ${webhookStatus === 'active' ? 'active' : ''}`}
                      onClick={() => setWebhookStatus('active')}
                    >
                      <CheckCircle size={14} />
                      Active
                    </button>
                    <button
                      className={`status-btn ${webhookStatus === 'paused' ? 'active' : ''}`}
                      onClick={() => setWebhookStatus('paused')}
                    >
                      <Pause size={14} />
                      Paused
                    </button>
                  </div>
                ) : (
                  <div className={`readonly-value status-value ${webhook?.status}`}>
                    {webhook?.status === 'active' && <CheckCircle size={14} />}
                    {webhook?.status === 'paused' && <Pause size={14} />}
                    {webhook?.status === 'disabled' && <AlertCircle size={14} />}
                    <span>{webhook?.status}</span>
                    {webhook?.failure_count && webhook.failure_count > 0 && (
                      <span className="failure-count">({webhook.failure_count} failures)</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Events Section */}
          <div className="section">
            <h3>Events *</h3>
            <p className="section-description">Select which events should trigger this webhook</p>

            <div className="events-grid">
              {Object.entries(groupedEvents).map(([category, events]) => (
                <div key={category} className="event-category">
                  <div className="category-header">{category}</div>
                  {events.map(event => (
                    <label key={event} className={`event-checkbox ${!isEditing ? 'readonly' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(event)}
                        onChange={() => isEditing && toggleEvent(event)}
                        disabled={!isEditing}
                      />
                      <span className="event-label">{event}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Index Filter Section */}
          {indexes.length > 0 && (
            <div className="section">
              <h3>Index Filter (Optional)</h3>
              <p className="section-description">Limit webhook to specific indexes. Leave empty for all indexes.</p>

              <div className="indexes-list">
                {indexes.map(index => (
                  <label key={index.index_id} className={`index-checkbox ${!isEditing ? 'readonly' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedIndexIds.includes(index.index_id)}
                      onChange={() => isEditing && toggleIndex(index.index_id)}
                      disabled={!isEditing}
                    />
                    <span className="index-label">{index.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Secret Management */}
          {!isCreating && webhook && (
            <div className="section">
              <div className="section-header-row">
                <h3>Signing Secret</h3>
                <button
                  className="rotate-secret-btn"
                  onClick={handleRotateSecret}
                  disabled={isRotatingSecret || isEditing}
                >
                  {isRotatingSecret ? (
                    <RefreshCw size={14} className="spinning" />
                  ) : (
                    <RotateCcw size={14} />
                  )}
                  Rotate Secret
                </button>
              </div>
              <p className="section-description">
                Use this secret to verify webhook signatures. Each request includes an X-Webhook-Signature header.
              </p>
            </div>
          )}

          {/* Metadata Section */}
          {!isCreating && webhook && (
            <div className="section">
              <h3>Webhook Info</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDate(webhook.created_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Updated</span>
                  <span className="info-value">{formatDate(webhook.updated_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Success</span>
                  <span className="info-value success">{formatDate(webhook.last_success_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Last Failure</span>
                  <span className="info-value error">{formatDate(webhook.last_failure_at)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Deliveries Section */}
          {!isCreating && webhook && (
            <div className="section deliveries-section">
              <div className="section-header-row">
                <h3>Recent Deliveries</h3>
                <div className="delivery-controls">
                  <select
                    value={deliveryFilter}
                    onChange={(e) => {
                      const nextFilter = e.target.value;
                      deliveryOwnerKeyRef.current = buildAsyncRequestOwnerKey(
                        webhook.webhook_id,
                        nextFilter,
                      );
                      setDeliveryFilter(nextFilter);
                      loadDeliveries(webhook.webhook_id, nextFilter);
                    }}
                    className="delivery-filter"
                  >
                    <option value="">All Statuses</option>
                    <option value="delivered">Delivered</option>
                    <option value="failed">Failed</option>
                    <option value="pending">Pending</option>
                  </select>
                  <button
                    className="refresh-btn"
                    onClick={() => loadDeliveries(webhook.webhook_id, deliveryFilter)}
                    disabled={isLoadingDeliveries}
                  >
                    <RefreshCw size={14} className={isLoadingDeliveries ? 'spinning' : ''} />
                  </button>
                </div>
              </div>

              <div className="deliveries-list">
                {deliveryError ? (
                  <div className="no-deliveries" role="alert">
                    {deliveryError}
                  </div>
                ) : isLoadingDeliveries ? (
                  <div className="loading-deliveries">Loading deliveries...</div>
                ) : deliveries.length === 0 ? (
                  <div className="no-deliveries">No deliveries yet</div>
                ) : (
                  deliveries.map(delivery => (
                    <div key={delivery.delivery_id} className="delivery-item">
                      <div
                        className="delivery-header"
                        onClick={() => toggleDeliveryExpansion(delivery.delivery_id)}
                      >
                        <div className={`delivery-status ${getDeliveryStatusClass(delivery.status)}`}>
                          {delivery.status === 'delivered' && <CheckCircle size={14} />}
                          {delivery.status === 'failed' && <AlertCircle size={14} />}
                          {delivery.status === 'pending' && <Clock size={14} />}
                          {delivery.status === 'retrying' && <RefreshCw size={14} />}
                          <span>{delivery.status}</span>
                        </div>
                        <span className="delivery-event">{delivery.event_type}</span>
                        <span className="delivery-time">{formatDate(delivery.created_at)}</span>
                        {expandedDeliveries.has(delivery.delivery_id) ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </div>

                      {expandedDeliveries.has(delivery.delivery_id) && (
                        <div className="delivery-details">
                          <div className="detail-row">
                            <span className="detail-label">Delivery ID</span>
                            <span className="detail-value">{delivery.delivery_id}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Attempts</span>
                            <span className="detail-value">{delivery.attempts}</span>
                          </div>
                          {delivery.response_status_code && (
                            <div className="detail-row">
                              <span className="detail-label">Response Code</span>
                              <span className="detail-value">{delivery.response_status_code}</span>
                            </div>
                          )}
                          {delivery.error_message && (
                            <div className="detail-row error">
                              <span className="detail-label">Error</span>
                              <span className="detail-value">{delivery.error_message}</span>
                            </div>
                          )}
                          {delivery.next_retry_at && (
                            <div className="detail-row">
                              <span className="detail-label">Next Retry</span>
                              <span className="detail-value">{formatDate(delivery.next_retry_at)}</span>
                            </div>
                          )}
                          {delivery.payload && (
                            <div className="detail-row payload-row">
                              <span className="detail-label">Payload</span>
                              <pre className="payload-content">{JSON.stringify(delivery.payload, null, 2)}</pre>
                            </div>
                          )}
                          {delivery.response_body && (
                            <div className="detail-row response-row">
                              <span className="detail-label">Response Body</span>
                              <pre className="response-content">{delivery.response_body}</pre>
                            </div>
                          )}
                          {(delivery.status === 'failed' || delivery.status === 'retrying') && (
                            <button
                              className="retry-btn"
                              onClick={() => handleRetryDelivery(delivery.delivery_id)}
                            >
                              <RotateCcw size={12} />
                              Retry Now
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="sidebar-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  if (isCreating) {
                    onClose();
                  } else {
                    setIsEditing(false);
                    if (webhook) {
                      setWebhookName(webhook.name);
                      setWebhookUrl(webhook.url);
                      setSelectedEvents(webhook.events || []);
                      setSelectedIndexIds(webhook.index_ids || []);
                      setWebhookStatus(webhook.status === 'disabled' ? 'paused' : webhook.status as 'active' | 'paused');
                    }
                  }
                }}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save size={16} />
                    {isCreating ? 'Create Webhook' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WebhookDetailSidebar;
